import type { CoachingUserRole } from "@vjal/api-contract";
import type {
  CoachingMembershipDto,
  CoachingProfileDto,
  OtpRequestResult,
  ProfileNotificationPreferences,
  ProfileUpdateResult
} from "./coaching-dtos";

/**
 * Adapters between the live platform/coaching auth payloads and the
 * frontend DTO shapes the screens consume. Mock services already return
 * DTO-shaped payloads, so every adapter passes DTO-shaped input through
 * unchanged (mock/live parity stays in the service facade).
 */

interface LiveAuthMeMembership {
  orgId?: unknown;
  role?: unknown;
  status?: unknown;
  orgName?: unknown;
  coachingRole?: unknown;
}
interface LiveAuthMeOrg extends LiveAuthMeMembership {
  roleHint?: unknown;
}

interface LiveAuthMeResponse {
  isPlatformAdmin?: unknown;
  user?: {
    id?: unknown;
    email?: unknown;
    firstName?: unknown;
    lastName?: unknown;
    phone?: unknown;
    title?: unknown;
    timezone?: unknown;
    notificationPreferences?: unknown;
  };
  memberships?: LiveAuthMeMembership[];
  coachingSession?: { orgs?: LiveAuthMeOrg[] };
}
interface LiveProfileUpdateResponse {
  profile?: {
    id?: unknown;
    email?: unknown;
    name?: unknown;
    phone?: unknown;
    title?: unknown;
    timezone?: unknown;
    notificationPreferences?: unknown;
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined;
}

function asCoachingRole(value: unknown): CoachingUserRole | null {
  return value === "super_admin" || value === "admin" || value === "coach" || value === "company_admin" || value === "cohort_lead" || value === "participant"
    ? value
    : null;
}

function normalizeMembership(membership: LiveAuthMeMembership): CoachingMembershipDto {
  return {
    orgId: asString(membership.orgId) ?? "",
    role: asString(membership.role) ?? null,
    status: asString(membership.status) ?? null,
    orgName: asString(membership.orgName) ?? null,
    coachingRole: asCoachingRole(membership.coachingRole)
  };
}

export function isLiveAuthMeResponse(payload: unknown): payload is LiveAuthMeResponse {
  return isRecord(payload) && isRecord(payload.user);
}

/** Mock services already answer with DTO-shaped `{ ok, status, ... }` results. */
export function isDtoShapedResult(payload: unknown): boolean {
  return isRecord(payload) && typeof payload.ok === "boolean";
}

export function toCoachingProfileDto(payload: unknown): CoachingProfileDto {
  if (!isLiveAuthMeResponse(payload)) return payload as CoachingProfileDto;

  const memberships = Array.isArray(payload.memberships) ? payload.memberships.map(normalizeMembership) : [];
  const orgs = Array.isArray(payload.coachingSession?.orgs) ? payload.coachingSession.orgs : [];
  const activeOrg = orgs.find((org) => org.status === "active") ?? orgs[0];
  const activeMembership = memberships.find((membership) => membership.orgId === activeOrg?.orgId) ?? memberships[0];
  const name = [asString(payload.user?.firstName), asString(payload.user?.lastName)].filter(Boolean).join(" ");
  const email = asString(payload.user?.email) ?? "";

  return {
    isPlatformAdmin: payload.isPlatformAdmin === true,
    id: asString(payload.user?.id) ?? "",
    email,
    name: name || email,
    role: activeMembership?.coachingRole ?? asString(activeOrg?.roleHint) ?? activeMembership?.role ?? "",
    orgId: activeMembership?.orgId ?? asString(activeOrg?.orgId),
    memberships,
    phone: asString(payload.user?.phone),
    title: asString(payload.user?.title),
    timezone: asString(payload.user?.timezone),
    notification_preferences: toNotificationPreferences(payload.user?.notificationPreferences)
  };
}

export function toOtpRequestResult(payload: unknown): OtpRequestResult {
  if (isRecord(payload) && typeof payload.ok === "boolean") return payload as unknown as OtpRequestResult;
  const succeeded = isRecord(payload) && payload.success === true;
  return { ok: succeeded, status: succeeded ? "requested" : "provider_closed" };
}

export function isLiveOtpVerifySuccess(payload: unknown): boolean {
  return isRecord(payload) && payload.success === true && typeof payload.ok !== "boolean";
}

export function toProfileUpdateResult(payload: unknown, currentProfile: CoachingProfileDto): ProfileUpdateResult {
  if (isRecord(payload) && typeof payload.ok === "boolean") return payload as unknown as ProfileUpdateResult;

  const saved = isRecord(payload) ? (payload as LiveProfileUpdateResponse).profile : undefined;
  if (!isRecord(saved)) return { ok: false, status: "validation_error" };

  return {
    ok: true,
    status: "saved",
    profile: {
      ...currentProfile,
      id: asString(saved.id) ?? currentProfile.id,
      email: asString(saved.email) ?? currentProfile.email,
      name: asString(saved.name) ?? currentProfile.name,
      title: asString(saved.title) ?? currentProfile.title,
      phone: asString(saved.phone) ?? currentProfile.phone,
      timezone: asString(saved.timezone) ?? currentProfile.timezone,
      notification_preferences:
        toNotificationPreferences(saved.notificationPreferences) ?? currentProfile.notification_preferences
    }
  };
}

function toNotificationPreferences(value: unknown): ProfileNotificationPreferences | undefined {
  if (!isRecord(value)) return undefined;
  return {
    session_reminders: value.session_reminders === true,
    calendar_alerts: value.calendar_alerts === true,
    weekly_digest: value.weekly_digest === true
  };
}
